import { forwardRef } from 'react'
import { Box, Typography, Chip, CircularProgress, Alert, Button, Stack } from '@mui/material'
import {
  CheckCircle as CheckIcon,
  ReportProblem as WarnIcon,
  FlagCircle as FlagIcon,
  Replay as RetryIcon,
} from '@mui/icons-material'
import type { MockInterviewHistoryEntry } from '../../services/api'

interface MockInterviewFeedbackProps {
  entry: MockInterviewHistoryEntry
  /** 'client' shows the encouraging summary; 'coach' shows the clinical one. */
  viewer: 'client' | 'coach'
  /** Called when coach/client clicks "retry feedback" on a failed generation. */
  onRetry?: () => void
  /** True when a retry request is in flight. */
  retrying?: boolean
}

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  if (m === 0) return `${s}s`
  return s > 0 ? `${m}m ${s}s` : `${m}m`
}

const MockInterviewFeedback = forwardRef<HTMLDivElement, MockInterviewFeedbackProps>(
  function MockInterviewFeedback({ entry, viewer, onRetry, retrying }, ref) {
    const header = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" fontWeight={700}>
          Mock Interview Feedback
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatDateTime(entry.date)} &middot; {formatDuration(entry.duration)}
        </Typography>
      </Box>
    )

    // Generating / pending
    if (entry.feedbackStatus === 'pending' || entry.feedbackStatus === 'generating') {
      return (
        <Box
          ref={ref}
          sx={{
            p: 2.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          {header}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Reviewing your session. Feedback is coming. This takes about 20 to 60 seconds.
            </Typography>
          </Box>
        </Box>
      )
    }

    // Failed
    if (entry.feedbackStatus === 'failed' || (!entry.feedbackSummary && entry.feedbackStatus !== 'complete')) {
      return (
        <Box
          ref={ref}
          sx={{
            p: 2.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          {header}
          <Alert
            severity="warning"
            action={
              onRetry ? (
                <Button
                  size="small"
                  startIcon={<RetryIcon />}
                  onClick={onRetry}
                  disabled={retrying}
                >
                  {retrying ? 'Retrying...' : 'Retry'}
                </Button>
              ) : undefined
            }
          >
            Feedback did not generate. Use the Retry button. If it fails again, your strategist will review the session transcript directly.
          </Alert>
        </Box>
      )
    }

    const fb = entry.feedbackSummary
    if (!fb) return null

    const summary = viewer === 'coach' ? fb.coachFacingSummary : fb.clientFacingSummary
    const summaryLabel = viewer === 'coach' ? 'Coach summary' : 'Here is what your session showed.'

    return (
      <Box
        ref={ref}
        sx={{
          p: 2.5,
          border: '1px solid',
          borderColor: 'primary.light',
          borderRadius: 2,
          bgcolor: 'primary.50',
        }}
      >
        {header}

        {summary && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant={viewer === 'coach' ? 'caption' : 'subtitle2'}
              fontWeight={viewer === 'coach' ? 700 : 600}
              color="text.secondary"
              sx={{
                display: 'block',
                letterSpacing: viewer === 'coach' ? 0.5 : 'normal',
                textTransform: viewer === 'coach' ? 'uppercase' : 'none',
              }}
            >
              {summaryLabel}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {summary}
            </Typography>
          </Box>
        )}

        <Stack spacing={1.5}>
          {fb.priorityFocusAreas && fb.priorityFocusAreas.length > 0 && (
            <FeedbackList
              icon={<FlagIcon fontSize="small" sx={{ color: 'warning.main' }} />}
              title="What to work on before the real interview."
              items={fb.priorityFocusAreas}
              chipLabel={`${fb.priorityFocusAreas.length}`}
              chipColor="warning"
              viewer={viewer}
            />
          )}
          {fb.strengths && fb.strengths.length > 0 && (
            <FeedbackList
              icon={<CheckIcon fontSize="small" sx={{ color: 'success.main' }} />}
              title="Strengths"
              items={fb.strengths}
              chipLabel={`${fb.strengths.length}`}
              chipColor="success"
              viewer={viewer}
            />
          )}
          {fb.weaknesses && fb.weaknesses.length > 0 && (
            <FeedbackList
              icon={<WarnIcon fontSize="small" sx={{ color: 'error.main' }} />}
              title="Weaknesses"
              items={fb.weaknesses}
              chipLabel={`${fb.weaknesses.length}`}
              chipColor="error"
              viewer={viewer}
            />
          )}
        </Stack>
      </Box>
    )
  }
)

function FeedbackList({
  icon,
  title,
  items,
  chipLabel,
  chipColor,
  viewer,
}: {
  icon: React.ReactNode
  title: string
  items: string[]
  chipLabel: string
  chipColor: 'success' | 'warning' | 'error'
  viewer: 'client' | 'coach'
}) {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
        {icon}
        {/* MWA-284 — the client's headings are full sentences, so they render
            sentence-case here for the same reason the summary label above does.
            Staff keep the compact ALL-CAPS caption. */}
        <Typography
          variant={viewer === 'coach' ? 'caption' : 'subtitle2'}
          fontWeight={viewer === 'coach' ? 700 : 600}
          sx={{
            letterSpacing: viewer === 'coach' ? 0.3 : 'normal',
            textTransform: viewer === 'coach' ? 'uppercase' : 'none',
          }}
        >
          {title}
        </Typography>
        <Chip label={chipLabel} size="small" color={chipColor} sx={{ height: 18, fontSize: '0.65rem' }} />
      </Box>
      <Box component="ul" sx={{ pl: 3, m: 0, '& li': { mb: 0.5 } }}>
        {items.map((item, i) => (
          <li key={i}>
            <Typography variant="body2">{item}</Typography>
          </li>
        ))}
      </Box>
    </Box>
  )
}

export default MockInterviewFeedback
